/**
 * CanvasPageV2 — Parallel canvas route rebuilt on `@xyflow/react` + `elkjs`
 * hierarchical layout. Lives at `/canvas-v2` when the feature flag
 * `features.canvasV2` is set in localStorage; see `utils/featureFlags.js`.
 *
 * WHY THIS EXISTS
 * ───────────────
 * Both the legacy Sugiyama layout (`utils/dagLayout.js`) and the IR pipeline
 * (`utils/ir/*`) run into the same wall on real workflows: nested containers
 * (SWITCH-in-FORK, DO_WHILE-around-SWITCH, etc.) routinely produce container
 * chrome that overflows its parent, because both engines size containers
 * bottom-up without negotiating horizontal budget with siblings or with the
 * trunk anchor. Tasks #159–#163 replace both with ELK's layered algorithm
 * driven through a true hierarchical `children[]` tree, rendered by the
 * MIT-licensed, battle-tested React Flow component library.
 *
 * WHAT THIS FIRST CUT DOES  (task #159 Day 1 scaffold)
 * ────────────────────────
 *  - Accepts a pasted Code-tab JSON payload (the same `{nodes, connections,
 *    workflowMeta}` contract `WorkflowCodeTab.jsx` canonicalizes).
 *  - Runs it through the existing `elkLayoutDAG` (flat layered) from
 *    `utils/elkLayout.js` — the SAME layout helper the legacy canvas's ELK
 *    toggle uses. Hierarchical `children[]` nesting lands in #160.
 *  - Renders positioned nodes + ELK-routed edges with default React Flow
 *    node components. Styling + custom node shapes are task #160.
 *  - Zero container chrome, zero execution overlay, zero palette, zero
 *    drag-to-add — those land in #160–#163.
 *
 * WHAT THIS FIRST CUT EXPLICITLY IS NOT
 * ─────────────────────────────────────
 *  - It is not wired into navigation. Reach it by typing `/canvas-v2` after
 *    `localStorage.setItem('features.canvasV2','true')`.
 *  - It does not save, load from backend, or emit Code-tab JSON back. The
 *    paste box is one-way for now.
 *  - It does not yet attempt geometric parity with Conductor OSS; the 29-node
 *    `complex_claim_stress_test` rendering here is a smoke test.
 *
 * DESIGN NOTE ON THE INPUT PAYLOAD
 * ────────────────────────────────
 * The Code-tab payload carries `_containerBounds` and `_containerChildren`
 * hints on container heads (emitted by the IR pipeline's irToFlat). V2
 * DELIBERATELY IGNORES them in this first cut — the whole point of the
 * rewrite is to let ELK compute container geometry from a `children[]`
 * tree instead of trusting IR's stamped bounds. When task #160 lands the
 * hierarchical ELK graph, it will re-derive nesting from `_containerChildren`
 * as input (tree structure hint) but NOT from `_containerBounds` (stale
 * geometry from the broken pipeline).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import ELK from 'elkjs/lib/elk.bundled.js';
import { elkLayoutDAG } from '../utils/elkLayout';
import { flatToElkHierarchical } from '../utils/v2/flatToElkHierarchical';
import { elkToReactFlow } from '../utils/v2/elkToReactFlow';
import { lintCanvas, diagnosticsByNodeId, PAYLOAD_NODE_ID, SEVERITY } from '../utils/v2/v2Lint';
import { V2_NODE_TYPES } from './v2/nodes/V2Nodes';
import V2Edge from './v2/edges/V2Edge';
import { V2_PAL, V2_BRAND, V2_STONE } from './v2/nodes/v2Palette';
import { lookupChildCanvas } from './v2/childWorkflowRegistry';
import CommandPalette from './v2/CommandPalette';
import NodeConfigPanel from './v2/NodeConfigPanel';
import { computeDiff, buildUnionPayload } from '../utils/v2/diffPayload';
import { STRESS_TEST_BASE } from './v2/diffDemoFixture';
import COMPLEX_CLAIM_STRESS_TEST from '../utils/v2/__tests__/fixtures/complex_claim_stress_test.json';
import {
  getWorkflow, saveCanvas,
  getRunDagSnapshot, getNodeExecutions, getWorkflowRun,
} from '../services/api';
import { connectWorkflowWebSocket, disconnectWorkflowWebSocket } from '../services/websocket';

const NODE_W = 260;
const NODE_H = 72;

// Singleton ELK for the hierarchical path. The flat elkLayoutDAG keeps its
// own instance; this one is dedicated to the hierarchical V2 pipeline so
// we can tune it independently later (different algorithm defaults etc.).
let _hierElk = null;
function getHierElk() {
  if (!_hierElk) _hierElk = new ELK();
  return _hierElk;
}

const V2_EDGE_TYPES = { v2Edge: V2Edge };

// Layout modes:
//   'flat'         — the original scaffold path: elkLayoutDAG over raw {nodes, connections}
//   'hierarchical' — task #160: flatToIR → hierarchical ELK → custom RF nodes
// Default flipped to hierarchical now that the Switch construct lands.
const DEFAULT_MODE = 'hierarchical';

/**
 * Built-in fixture for first render. Exercises BOTH constructs delivered
 * so far — static parallel_fork + join (#161) and switch (#160) — so any
 * regression in either chrome is visible on page load.
 *
 * Flow:
 *   Start → fetch_order
 *         → Fork [validate_addr | check_inventory → reserve_items | calculate_tax]
 *         → Join (ALL_COMPLETED)
 *         → Switch on $.status
 *             approved → charge_card → ship
 *             declined → notify_customer
 *             default  → escalate
 *         → End
 *
 * Uses the IR-canonical {source, target, sourceHandle} connection shape.
 */
const DEFAULT_FIXTURE = {
  workflowMeta: {
    name: 'v2_fork_switch_smoke',
    description: 'Static fork + join (#161) feeding a 3-case switch (#160). Default smoke fixture.',
  },
  nodes: [
    { id: 'n_start', type: 'trigger', label: 'Start', config: {} },
    { id: 'n_fetch', type: 'http_task', label: 'Fetch order', config: { method: 'GET', url: 'https://api.example.com/orders/{{orderId}}' } },
    // Static fork — 3 parallel legs
    { id: 'n_fork', type: 'parallel_fork', label: 'Validate in parallel', config: { branchCount: 3 } },
    { id: 'n_validate_addr', type: 'http_task', label: 'Validate address', config: { method: 'POST', url: 'https://api.example.com/addr/check' } },
    { id: 'n_check_inv',    type: 'http_task', label: 'Check inventory',  config: { method: 'GET',  url: 'https://api.example.com/inventory' } },
    { id: 'n_reserve',      type: 'script_task', label: 'Reserve items',   config: {} },
    { id: 'n_calc_tax',     type: 'script_task', label: 'Calculate tax',   config: {} },
    { id: 'n_join', type: 'parallel_join', label: 'Await all', config: { joinMode: 'ALL_COMPLETED', inputMapping: { validate_addr: 'n_validate_addr', check_inv: 'n_reserve', calc_tax: 'n_calc_tax' } } },
    // Switch on order status
    { id: 'n_switch', type: 'condition', label: 'Route on status', config: { expression: '$.status' } },
    { id: 'n_charge',   type: 'http_task', label: 'Charge card',        config: { method: 'POST', url: 'https://api.example.com/charge' } },
    { id: 'n_ship',     type: 'http_task', label: 'Ship order',         config: { method: 'POST', url: 'https://api.example.com/ship' } },
    { id: 'n_notify',   type: 'email',     label: 'Notify customer',    config: {} },
    { id: 'n_escalate', type: 'script_task', label: 'Escalate to ops',  config: {} },
    { id: 'n_end', type: 'end', label: 'Done', config: { status: 'completed' } },
  ],
  connections: [
    { source: 'n_start', target: 'n_fetch', sourceHandle: 'default' },
    { source: 'n_fetch', target: 'n_fork',  sourceHandle: 'default' },
    // Fork legs — sourceHandle conveys branch name
    { source: 'n_fork', target: 'n_validate_addr', sourceHandle: 'validate_addr' },
    { source: 'n_fork', target: 'n_check_inv',    sourceHandle: 'check_inv' },
    { source: 'n_fork', target: 'n_calc_tax',     sourceHandle: 'calc_tax' },
    // Leg 2 chain: check_inv → reserve
    { source: 'n_check_inv', target: 'n_reserve', sourceHandle: 'default' },
    // All leg tails → join
    { source: 'n_validate_addr', target: 'n_join', sourceHandle: 'default' },
    { source: 'n_reserve',       target: 'n_join', sourceHandle: 'default' },
    { source: 'n_calc_tax',      target: 'n_join', sourceHandle: 'default' },
    // Join → switch
    { source: 'n_join',   target: 'n_switch', sourceHandle: 'default' },
    // Switch cases
    { source: 'n_switch', target: 'n_charge',   sourceHandle: 'approved' },
    { source: 'n_switch', target: 'n_notify',   sourceHandle: 'declined' },
    { source: 'n_switch', target: 'n_escalate', sourceHandle: 'default' },
    // approved-case chain: charge → ship
    { source: 'n_charge', target: 'n_ship', sourceHandle: 'default' },
    // Case exits converge to end
    { source: 'n_ship',     target: 'n_end', sourceHandle: 'default' },
    { source: 'n_notify',   target: 'n_end', sourceHandle: 'default' },
    { source: 'n_escalate', target: 'n_end', sourceHandle: 'default' },
  ],
};

/**
 * Dynamic-fork fixture — loadable via the "Load dyn-fork" button to flex
 * the violet chrome + listExpression + concurrencyLimit surfaces.
 *
 * Flow: Start → list_items → DynamicFork (listExpression=$.items, max=5)
 *               → Join (ALL) → summarize → End
 *
 * With no `dynamicTasks` resolved at def time, the builder collapses the
 * legs into a single "template" leg showing what each runtime task would
 * look like.
 */
const DYN_FORK_FIXTURE = {
  workflowMeta: {
    name: 'v2_dyn_fork_smoke',
    description: 'Dynamic fork with unresolved branches — template leg + listExpression visible.',
  },
  nodes: [
    { id: 'n_start', type: 'trigger', label: 'Start', config: {} },
    { id: 'n_list', type: 'http_task', label: 'Fetch items', config: { method: 'GET', url: 'https://api.example.com/items' } },
    { id: 'n_dfork', type: 'parallel_fork', label: 'Process each', config: { isDynamic: true, listExpression: '$.items', concurrencyLimit: 5 } },
    { id: 'n_process_item', type: 'http_task', label: 'Process item', config: { method: 'POST', url: 'https://api.example.com/process' } },
    { id: 'n_djoin', type: 'parallel_join', label: 'Collect results', config: { joinMode: 'ALL_COMPLETED' } },
    { id: 'n_sum', type: 'script_task', label: 'Summarize', config: {} },
    { id: 'n_end', type: 'end', label: 'Done', config: {} },
  ],
  connections: [
    { source: 'n_start', target: 'n_list',  sourceHandle: 'default' },
    { source: 'n_list',  target: 'n_dfork', sourceHandle: 'default' },
    { source: 'n_dfork', target: 'n_process_item', sourceHandle: 'item' },
    { source: 'n_process_item', target: 'n_djoin', sourceHandle: 'default' },
    { source: 'n_djoin', target: 'n_sum', sourceHandle: 'default' },
    { source: 'n_sum',   target: 'n_end', sourceHandle: 'default' },
  ],
};

/**
 * DoWhile fixture (#162) — a classic retry-with-backoff loop.
 *
 *   Start → fetch_quote → DoWhile [ validate → score ] while $.needsMore
 *                      → finalize → End
 *
 * The back-edge from `n_score` → `n_loop` is the loop-back marker that tells
 * the builder where the iteration closes. The `exit` handle on `n_loop`
 * leaves the loop when the condition goes false.
 */
const DO_WHILE_FIXTURE = {
  workflowMeta: {
    name: 'v2_do_while_smoke',
    description: 'DoWhile container with a 2-task body, dashed orange self-loop back-edge, iteration hint.',
  },
  nodes: [
    { id: 'n_start',  type: 'trigger',    label: 'Start', config: {} },
    { id: 'n_fetch',  type: 'http_task',  label: 'Fetch quote', config: { method: 'GET', url: 'https://api.example.com/quote' } },
    { id: 'n_loop',   type: 'do_while',   label: 'Retry until confident', config: { loopCondition: '$.confidence < 0.9', iterationHint: 'max 5 iterations' } },
    { id: 'n_validate', type: 'script_task', label: 'Validate quote', config: {} },
    { id: 'n_score',    type: 'http_task',   label: 'Score confidence', config: { method: 'POST', url: 'https://api.example.com/score' } },
    { id: 'n_final',  type: 'script_task', label: 'Finalize result', config: {} },
    { id: 'n_end',    type: 'end',        label: 'Done',  config: {} },
  ],
  connections: [
    { source: 'n_start', target: 'n_fetch', sourceHandle: 'default' },
    { source: 'n_fetch', target: 'n_loop',  sourceHandle: 'default' },
    // Body: loop → validate → score → loop (back-edge)
    { source: 'n_loop',     target: 'n_validate', sourceHandle: 'default' },
    { source: 'n_validate', target: 'n_score',    sourceHandle: 'default' },
    { source: 'n_score',    target: 'n_loop',     sourceHandle: 'default' },   // loop_back
    // Exit path
    { source: 'n_loop',  target: 'n_final', sourceHandle: 'exit' },
    { source: 'n_final', target: 'n_end',   sourceHandle: 'default' },
  ],
};

/**
 * Inline-expanded SubWorkflow fixture (#162) — Canvas V2 drill-in. Conductor OSS
 * forces you to navigate away to see what a sub-workflow does; V2 shows the
 * nested DAG chrome right where it's called from.
 *
 *   Start → receive_request → SubWorkflow(kyc_check, embedded) → decide → End
 *
 * The embedded block below the sub-workflow head is a mini DAG that runs
 * as part of the parent flow at design time.
 */
const SUBWF_FIXTURE = {
  workflowMeta: {
    name: 'v2_subwf_smoke',
    description: 'SubWorkflow with inline-expanded body — indigo chrome, head carries refName+version.',
  },
  nodes: [
    { id: 'n_start', type: 'trigger',   label: 'Start', config: {} },
    { id: 'n_rx',    type: 'http_task', label: 'Receive request', config: { method: 'POST', url: 'https://api.example.com/intake' } },
    { id: 'n_sub',   type: 'sub_workflow', label: 'Run KYC check', config: {
      subWorkflowName: 'kyc_check',
      subWorkflowVersion: 3,
      async: false,
      // V2 drill-in preview — these render as a nested DAG inside the
      // SubWorkflow's chrome so you see the shape of what runs without
      // leaving the canvas (Conductor OSS forces a navigation).
      _embeddedDagPreview: [
        { id: 'kyc_verify_id', type: 'http_task',   label: 'Verify ID doc' },
        { id: 'kyc_sanctions', type: 'http_task',   label: 'Sanctions check' },
        { id: 'kyc_score',     type: 'script_task', label: 'Compute KYC score' },
      ],
    } },
    { id: 'n_decide', type: 'script_task', label: 'Decide', config: {} },
    { id: 'n_end',    type: 'end', label: 'Done', config: {} },
  ],
  connections: [
    { source: 'n_start', target: 'n_rx',    sourceHandle: 'default' },
    { source: 'n_rx',    target: 'n_sub',   sourceHandle: 'default' },
    { source: 'n_sub',   target: 'n_decide', sourceHandle: 'default' },
    { source: 'n_decide', target: 'n_end',  sourceHandle: 'default' },
  ],
};

/**
 * Flat mode — the original #159 scaffold path. ELK flat layered over raw
 * `{nodes, connections}`, default React Flow node component. Retained as
 * a fallback so toggling back is one dropdown click when we want to
 * compare with the hierarchical output.
 */
async function layoutFlat(payload) {
  const nodes = Array.isArray(payload?.nodes) ? payload.nodes : [];
  const connections = Array.isArray(payload?.connections) ? payload.connections : [];

  if (nodes.length === 0) return { nodes: [], edges: [], error: null };

  try {
    const { nodes: laidOut, edges: laidOutEdges } = await elkLayoutDAG(
      nodes,
      connections,
      { nodeWidth: NODE_W, nodeHeight: NODE_H, rankSep: 100, nodeSep: 60, padding: 40 }
    );
    const rfNodes = laidOut.map((n) => ({
      id: String(n.id),
      position: { x: n.x ?? 0, y: n.y ?? 0 },
      data: { label: n.label || n.id, type: n.type || 'task' },
      type: 'default',
      width: n.width ?? NODE_W,
      height: n.height ?? NODE_H,
      style: { width: n.width ?? NODE_W, minHeight: n.height ?? NODE_H },
    }));
    const rfEdges = laidOutEdges.map((e, i) => ({
      id: e.id || `e_${i}_${e.from}__${e.to}`,
      source: String(e.from),
      target: String(e.to),
      label: e.label || undefined,
      type: 'smoothstep',
    }));
    return { nodes: rfNodes, edges: rfEdges, error: null };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[CanvasPageV2] flat ELK layout failed', err);
    return { nodes: [], edges: [], error: err?.message || String(err) };
  }
}

// Render a duration in ms as "1.2s" / "12.4s" / "3m 12s" / "1h 04m" —
// used by the execution banner to surface elapsed time succinctly.
function formatDuration(ms) {
  if (typeof ms !== 'number' || !isFinite(ms) || ms < 0) return '';
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)}s`;
  const m = Math.floor(s / 60);
  const sr = Math.round(s % 60);
  if (m < 60) return `${m}m ${String(sr).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  const mr = m % 60;
  return `${h}h ${String(mr).padStart(2, '0')}m`;
}

// Render a Date as "just now" / "3m ago" / "2h ago" / "Apr 24" — used by
// the workflow-IO strip to surface freshness without an absolute time.
function formatRelative(date) {
  if (!date) return '';
  const ms = Date.now() - date.getTime();
  if (ms < 60_000) return 'just now';
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Strip V2-only hints (drill-in expansion / diff status / fixture preview)
// from a node's config before saving — these are runtime overlay state
// that does not belong in persisted workflow definitions.
function stripV2OnlyHints(n) {
  if (!n || !n.config || typeof n.config !== 'object') return n;
  const cfg = { ...n.config };
  delete cfg._diffStatus;
  delete cfg._embeddedCanvas;
  delete cfg._embeddedDagPreview;
  return { ...n, config: cfg };
}
// Edges may carry a `_diffStatus` from the diff overlay; strip on save.
function stripV2OnlyEdgeHints(e) {
  if (!e || typeof e !== 'object') return e;
  const { _diffStatus, ...rest } = e;
  return rest;
}

/**
 * SubWorkflow inline drill-in (#168) — given the current payload and the
 * set of expanded SubWorkflow source-node ids, return a shallow-cloned
 * payload whose expanded SubWorkflow nodes carry `config._embeddedCanvas`
 * pointing at the child workflow's full `{nodes, connections}` payload.
 *
 * The builder's `_emitSubWorkflow` reads `_embeddedCanvas` and recursively
 * builds the child's IR, so the child's full DAG (with its own switches /
 * forks / loops) nests inside the parent's SubWorkflow container chrome
 * on the rendered canvas — no page navigation required.
 *
 * Payload is cloned at the node level only (not deep-cloned) — we mutate
 * `config` to add `_embeddedCanvas`, so each expanded node gets its own
 * config object to avoid leaking into the registry fixture. When no nodes
 * are expanded, returns the input unchanged to skip the work.
 *
 * @param {object} payload {nodes, connections}
 * @param {Set<string>} expandedSet set of canvas node ids to expand
 * @returns {object} payload with `_embeddedCanvas` stamped on expanded subwf nodes
 */
function injectExpandedChildren(payload, expandedSet) {
  if (!payload || !Array.isArray(payload.nodes) || !expandedSet || expandedSet.size === 0) {
    return payload;
  }
  let mutated = false;
  const nextNodes = payload.nodes.map((n) => {
    if (!n || !n.id || !expandedSet.has(n.id)) return n;
    if (n.type !== 'sub_workflow' && n.type !== 'async_sub_workflow') return n;
    const cfg = n.config || {};
    // Respect an already-present child payload (e.g. tests pre-injecting).
    if (cfg._embeddedCanvas) return n;
    const childCanvas = lookupChildCanvas(cfg.subWorkflowName || cfg.name, cfg.subWorkflowVersion, n.label);
    if (!childCanvas) return n;
    mutated = true;
    return {
      ...n,
      config: { ...cfg, _embeddedCanvas: childCanvas },
    };
  });
  if (!mutated) return payload;
  return { ...payload, nodes: nextNodes };
}

/**
 * Hierarchical mode — task #160. Walks the payload through flatToIR, emits
 * an ELK hierarchical graph with Switch containers, runs ELK, and converts
 * the result to React Flow nodes/edges that the V2 custom components know
 * how to render.
 */
async function layoutHierarchical(payload) {
  const nodes = Array.isArray(payload?.nodes) ? payload.nodes : [];
  const connections = Array.isArray(payload?.connections) ? payload.connections : [];
  if (nodes.length === 0) return { nodes: [], edges: [], error: null };

  try {
    const graph = flatToElkHierarchical({ nodes, connections });
    const laid = await getHierElk().layout(graph);
    // eslint-disable-next-line no-console
    console.log('[CanvasPageV2][hier] ELK bbox',
      Math.round(laid.width || 0), '×', Math.round(laid.height || 0),
      '| top-level children', (laid.children || []).length);
    const { rfNodes, rfEdges } = elkToReactFlow(laid);
    return { nodes: rfNodes, edges: rfEdges, error: null };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[CanvasPageV2] hierarchical layout failed', err);
    return { nodes: [], edges: [], error: err?.message || String(err) };
  }
}

/**
 * Dispatch — pick the right layout pipeline based on the mode.
 */
async function layoutForReactFlow(payload, mode) {
  if (mode === 'flat') return layoutFlat(payload);
  return layoutHierarchical(payload);
}

function CanvasV2Inner() {
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState([]);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState([]);
  const [jsonText, setJsonText] = useState(() => JSON.stringify(DEFAULT_FIXTURE, null, 2));
  const [parseError, setParseError] = useState(null);
  const [layoutError, setLayoutError] = useState(null);
  const [isLaying, setIsLaying] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const [mode, setMode] = useState(DEFAULT_MODE);
  // #168 — SubWorkflow inline drill-in state. Set of source-canvas node ids
  // whose SubWorkflows the user has expanded. Rendered as a breadcrumb at
  // the top of the canvas so the nesting depth is always visible.
  const [expandedSubworkflows, setExpandedSubworkflows] = useState(() => new Set());
  // #171 — Cmd+K command palette visibility. Opened by the global
  // keyboard shortcut or by a button; rendered inside the ReactFlow
  // provider so it has access to `useReactFlow` for viewport jumps.
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  // When the palette pans to a node, we attach a transient 'v2-flash'
  // className so the node briefly pulses a terracotta ring. Tracks the
  // rf node id (NOT the flat-canvas source id) since that's what the
  // ReactFlow renderer keys on.
  const [flashNodeRfId, setFlashNodeRfId] = useState(null);
  // #170 — Version diff state. When `diffBasePayload` is non-null we
  // layout the UNION (current merged with base-only nodes), stamp each
  // rf node with its diff status, and surface a summary banner at the
  // top of the canvas. Exit with "collapse diff" or by reloading a
  // fixture via the quick-load buttons.
  const [diffBasePayload, setDiffBasePayload] = useState(null);
  const [diffSummary, setDiffSummary] = useState(null);
  // #179 — Save/load wiring. When the page mounts with `?workflowId=X`
  // we hydrate from the backend; Save serialises lastPayloadRef back to
  // the same workflow. `dirty` tracks whether the canvas has diverged
  // from the last saved snapshot. `saveStatus` carries the most recent
  // save/load outcome so the UI can surface success/error toasts inline.
  const [workflowId, setWorkflowId] = useState(null);
  const [workflowName, setWorkflowName] = useState(null);
  const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(false);
  const [isSavingWorkflow, setIsSavingWorkflow] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // {kind:'ok'|'err', message:string}
  // #180 — Live execution streaming. When the URL carries `?runId=X` we
  // hydrate from the run's frozen DAG snapshot + node-execution history,
  // stamp execStatus on each rf node, then (if the run is still active)
  // open a STOMP WebSocket to /topic/workflow-execution/{runId} and
  // patch rf nodes as events arrive. The page becomes read-only —
  // saves and edits are disabled in execution view.
  const [executionRunId, setExecutionRunId] = useState(null);
  const [executionStatus, setExecutionStatus] = useState(null); // 'running' | 'completed' | 'failed' | 'cancelled'
  const [executionStartedAt, setExecutionStartedAt] = useState(null);
  const [executionCompletedAt, setExecutionCompletedAt] = useState(null);
  const [isExecutionMode, setIsExecutionMode] = useState(false);
  // #181 — node config editor. Holds the canvas-shape node (NOT the rf
  // node) that the user clicked; the panel reads/writes against this
  // draft and on Apply the parent merges the change into lastPayloadRef.
  const [editingNode, setEditingNode] = useState(null);
  // Payload-level lint diagnostics (the ones keyed to __payload__, e.g.
  // lint/parse-failed). Per-node diagnostics ride on each rfNode's data.
  const [payloadDiagnostics, setPayloadDiagnostics] = useState([]);
  // All diagnostics (both payload-level and per-node) — used for the stats
  // summary pill so customers can see at a glance whether the canvas has
  // ANY issues worth looking at.
  const [lintSummary, setLintSummary] = useState({ error: 0, warn: 0, info: 0 });
  const lastPayloadRef = useRef(DEFAULT_FIXTURE);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const runLayout = useCallback(async (payload, nextMode, nextExpanded, nextDiffBase) => {
    if (!mountedRef.current) return;
    lastPayloadRef.current = payload;
    const expandedForThisRun = nextExpanded || expandedSubworkflows;
    // Inject child canvases for any currently-expanded SubWorkflow nodes.
    // The builder (`_emitSubWorkflow`) picks up `config._embeddedCanvas`
    // and recursively nests the child's full IR inside the parent's
    // SubWorkflow container — Canvas V2's drill-in affordance (#168).
    let stampedPayload = injectExpandedChildren(payload, expandedForThisRun);

    // #170 — version diff. When a base payload is set we run the diff
    // and layout the UNION instead of the current payload alone. The
    // union merges removed-from-current nodes back in so reviewers can
    // see where they used to sit; every node carries `config._diffStatus`
    // which we later translate to a className on the rf node.
    const diffBase = nextDiffBase !== undefined ? nextDiffBase : diffBasePayload;
    let localDiffSummary = null;
    if (diffBase) {
      const d = computeDiff(diffBase, stampedPayload);
      stampedPayload = buildUnionPayload(diffBase, stampedPayload, d);
      localDiffSummary = { counts: d.counts };
    }

    setIsLaying(true);
    setLayoutError(null);
    const { nodes, edges, error } = await layoutForReactFlow(stampedPayload, nextMode || mode);
    if (!mountedRef.current) return;

    // ── Lint the payload and stamp per-node diagnostics ─────────────
    // Runs regardless of whether layout succeeded — the two pipelines
    // are independent, and the lint engine is guaranteed not to throw.
    // Post-processing mirrors the `execDemo` useEffect pattern: we
    // enrich rfNodes with a `data.diagnostics` field before commit.
    let decoratedNodes = nodes;
    let nextPayloadDiags = [];
    let nextSummary = { error: 0, warn: 0, info: 0 };
    try {
      const { diagnostics } = lintCanvas(payload);
      for (const d of diagnostics) {
        const key = String(d.severity || '').toLowerCase();
        if (key === SEVERITY.ERROR) nextSummary.error += 1;
        else if (key === SEVERITY.WARN) nextSummary.warn += 1;
        else if (key === SEVERITY.INFO) nextSummary.info += 1;
      }
      nextPayloadDiags = diagnostics.filter((d) => d.nodeId === PAYLOAD_NODE_ID);
      const perNode = diagnosticsByNodeId(
        diagnostics.filter((d) => d.nodeId !== PAYLOAD_NODE_ID),
      );
      decoratedNodes = nodes.map((n) => {
        // IR nodes carry a `sourceNodeId` pointer back to the flat-canvas
        // id. Fall back to n.id so scalar tiles (rendered directly) still
        // pick up their diagnostics.
        const srcId = (n.data && n.data.sourceNodeId) || n.id;
        const diags = perNode.get(srcId);
        if (!diags || diags.length === 0) return n;
        return { ...n, data: { ...n.data, diagnostics: diags } };
      });
    } catch (err) {
      // Lint should never break the canvas — swallow and log.
      // eslint-disable-next-line no-console
      console.error('[CanvasPageV2] lint pass failed', err);
    }

    // Stamp per-rf-node diffStatus + className so global CSS tints the
    // chrome. Looks up `_diffStatus` from the stamped payload by source
    // canvas id (which rf nodes preserve via `data.sourceNodeId`). This
    // happens after lint decoration so both signals survive into render.
    if (diffBase) {
      const statusByNodeId = new Map();
      for (const n of stampedPayload.nodes) {
        if (n && n.id && n.config && n.config._diffStatus) {
          statusByNodeId.set(n.id, n.config._diffStatus);
        }
      }
      decoratedNodes = decoratedNodes.map((n) => {
        const srcId = (n.data && n.data.sourceNodeId) || n.id;
        const status = statusByNodeId.get(srcId);
        if (!status || status === 'unchanged') return n;
        const prev = (n.className || '').replace(/(^|\s)v2-diff-(added|removed|modified)(\s|$)/g, ' ').trim();
        const cls = `v2-diff-${status}`;
        const next = prev ? `${prev} ${cls}` : cls;
        return { ...n, className: next, data: { ...n.data, diffStatus: status } };
      });
    }

    setRfNodes(decoratedNodes);
    setRfEdges(edges);
    setPayloadDiagnostics(nextPayloadDiags);
    setLintSummary(nextSummary);
    setDiffSummary(localDiffSummary);
    if (error) setLayoutError(error);
    setIsLaying(false);
  }, [setRfNodes, setRfEdges, mode, expandedSubworkflows, diffBasePayload]);

  // ── Load + save (#179) ─────────────────────────────────────────────
  // `loadWorkflow` hydrates from `GET /workflows/{id}` and lays out the
  // returned definition. `saveWorkflow` flushes the current canvas back
  // via `PUT /workflows/{id}/canvas`. Both clear the dirty flag and
  // surface a transient saveStatus toast in the left panel header.
  // Defined before the bootstrap effect so the URL-load path can call it.
  const loadWorkflow = useCallback(async (id) => {
    if (!id) return;
    setIsLoadingWorkflow(true);
    setSaveStatus(null);
    const res = await getWorkflow(id);
    if (!res || !res.ok) {
      setIsLoadingWorkflow(false);
      setSaveStatus({ kind: 'err', message: (res && res.error) || 'Failed to load workflow' });
      return;
    }
    const wf = res.data || {};
    const definition = wf.definition || { nodes: [], connections: [] };
    setWorkflowId(wf.id || id);
    setWorkflowName(wf.name || null);
    setSavedAt(wf.updatedAt ? new Date(wf.updatedAt) : null);
    setDirty(false);
    setExpandedSubworkflows(new Set());
    setDiffBasePayload(null);
    setJsonText(JSON.stringify(definition, null, 2));
    setSaveStatus({ kind: 'ok', message: `Loaded "${wf.name || id}"` });
    // Lay out the loaded definition. runLayout handles its own loading
    // state and updates lastPayloadRef.
    await runLayout(definition, mode, undefined, null);
    setIsLoadingWorkflow(false);
  }, [runLayout, mode]);

  // Save the currently-displayed payload to the active workflowId.
  // Always sends the canonicalised lastPayloadRef rather than the raw
  // textarea content — this is "save what's actually on the canvas",
  // matching legacy semantics (CanvasPage.jsx Ctrl+S sends the IR).
  const saveWorkflow = useCallback(async () => {
    if (!workflowId) {
      setSaveStatus({ kind: 'err', message: 'No workflow loaded — open one via ?workflowId=…' });
      return;
    }
    const payload = lastPayloadRef.current;
    if (!payload) {
      setSaveStatus({ kind: 'err', message: 'Nothing to save — apply a payload first' });
      return;
    }
    setIsSavingWorkflow(true);
    setSaveStatus(null);
    // Send only the canvas-shape fields the backend's /canvas endpoint
    // expects: {nodes, connections} plus optional dataFlow. Strip our
    // internal demo hints so they don't pollute persisted definitions.
    const wireBody = {
      nodes: Array.isArray(payload.nodes) ? payload.nodes.map(stripV2OnlyHints) : [],
      connections: Array.isArray(payload.connections) ? payload.connections.map(stripV2OnlyEdgeHints) : [],
    };
    if (Array.isArray(payload.dataFlow)) wireBody.dataFlow = payload.dataFlow;
    const res = await saveCanvas(workflowId, wireBody);
    setIsSavingWorkflow(false);
    if (!res || !res.ok) {
      setSaveStatus({ kind: 'err', message: (res && res.error) || 'Save failed' });
      return;
    }
    setDirty(false);
    setSavedAt(new Date());
    setSaveStatus({ kind: 'ok', message: 'Saved' });
  }, [workflowId]);

  // Mark dirty whenever the canvas state diverges from the last save.
  // Apply, fixture loads, and Canonicalize all trigger this — anything
  // that mutates lastPayloadRef should set dirty (except the load itself,
  // which clears it).
  const markDirty = useCallback(() => setDirty(true), []);

  // ── Execution streaming (#180) ─────────────────────────────────────
  // Patch a single rf node's exec data when an event arrives. Matches by
  // `data.sourceNodeId` (V2's rf-id is `kind__sourceId`, but the wire
  // events carry the original canvas nodeId — so source id is the join
  // key). Lowercases the wire status to match V2's existing exec palette
  // keys (running / completed / failed / skipped / cancelled).
  const patchNodeExecState = useCallback((nodeId, patch) => {
    if (!nodeId) return;
    setRfNodes((prev) => prev.map((n) => {
      const srcId = n.data && n.data.sourceNodeId;
      if (srcId !== nodeId) return n;
      return { ...n, data: { ...(n.data || {}), ...patch } };
    }));
  }, [setRfNodes]);

  // Translate a wire-shaped node-execution event into the patch our
  // rf nodes already understand (execStatus + execDurationMs).
  const applyNodeExecEvent = useCallback((evt) => {
    if (!evt || !evt.nodeId) return;
    const status = (evt.status || '').toString().toLowerCase() || null;
    const durationMs =
      typeof evt.durationMs === 'number' ? evt.durationMs
      : (evt.startedAt && evt.completedAt) ? (evt.completedAt - evt.startedAt)
      : null;
    patchNodeExecState(evt.nodeId, {
      execStatus: status,
      execDurationMs: durationMs,
    });
  }, [patchNodeExecState]);

  // Load a finished-or-running execution by id. Snapshot-first: pull
  // the frozen DAG + per-node state via REST, then (if the run is still
  // active) open the live WebSocket. Mirrors the legacy canvas pattern.
  const loadExecution = useCallback(async (runId) => {
    if (!runId) return;
    setIsLoadingWorkflow(true);
    setSaveStatus(null);
    setIsExecutionMode(true);
    setExecutionRunId(runId);

    // Three parallel REST fetches — none depend on each other.
    const [snapshotRes, nodeExecRes, runMetaRes] = await Promise.all([
      getRunDagSnapshot(runId),
      getNodeExecutions(runId),
      getWorkflowRun(runId),
    ]);

    setIsLoadingWorkflow(false);

    if (!snapshotRes || !snapshotRes.ok || !snapshotRes.data) {
      setSaveStatus({ kind: 'err', message: 'Failed to load execution snapshot' });
      setIsExecutionMode(false);
      return;
    }

    // The snapshot endpoint returns the workflow definition that ran.
    // Backend may wrap it in `.dagSnapshot` or `.definition` depending
    // on version; tolerate both shapes.
    const snapshot = snapshotRes.data;
    const definition =
      (snapshot.dagSnapshot && (snapshot.dagSnapshot.definition || snapshot.dagSnapshot)) ||
      snapshot.definition ||
      snapshot;
    if (!definition || !Array.isArray(definition.nodes) || !Array.isArray(definition.connections)) {
      setSaveStatus({ kind: 'err', message: 'Execution snapshot has no { nodes, connections }' });
      setIsExecutionMode(false);
      return;
    }

    // Run-level metadata (status, started/completed) — drives the banner.
    const meta = (runMetaRes && runMetaRes.ok && runMetaRes.data) || {};
    const runStatus = (meta.status || '').toString().toLowerCase() || 'unknown';
    setExecutionStatus(runStatus);
    setExecutionStartedAt(meta.startedAt ? new Date(meta.startedAt) : null);
    setExecutionCompletedAt(meta.completedAt ? new Date(meta.completedAt) : null);

    // Render the snapshot (read-only — no diff, no expansion).
    setExpandedSubworkflows(new Set());
    setDiffBasePayload(null);
    setJsonText(JSON.stringify(definition, null, 2));
    await runLayout(definition, mode, undefined, null);

    // Stamp historic per-node states from the REST snapshot. Wire status
    // is uppercase; lowercase to match the V2 palette keys.
    const nodeExecs = (nodeExecRes && nodeExecRes.ok && Array.isArray(nodeExecRes.data))
      ? nodeExecRes.data : [];
    if (nodeExecs.length > 0) {
      setRfNodes((prev) => {
        const byId = new Map();
        for (const ne of nodeExecs) {
          if (!ne || !ne.nodeId) continue;
          byId.set(ne.nodeId, ne);
        }
        return prev.map((n) => {
          const srcId = n.data && n.data.sourceNodeId;
          const ne = byId.get(srcId);
          if (!ne) return n;
          const status = (ne.status || '').toString().toLowerCase() || null;
          const durationMs =
            typeof ne.durationMs === 'number' ? ne.durationMs
            : (ne.startedAt && ne.completedAt) ? (ne.completedAt - ne.startedAt)
            : null;
          return { ...n, data: { ...(n.data || {}), execStatus: status, execDurationMs: durationMs } };
        });
      });
    }

    setSaveStatus({ kind: 'ok', message: `Execution ${runStatus} — ${nodeExecs.length} node states loaded` });
  }, [runLayout, mode, setRfNodes]);

  // Open the live stream when the run is active. Re-runs whenever
  // executionRunId / executionStatus changes; the cleanup disconnects
  // the WebSocket on unmount or when the run reaches a terminal state.
  useEffect(() => {
    if (!executionRunId) return;
    const isActive =
      executionStatus === 'running' ||
      executionStatus === 'pending' ||
      executionStatus === 'in_progress';
    if (!isActive) return;
    connectWorkflowWebSocket(executionRunId, {
      onNodeUpdate: applyNodeExecEvent,
      onWorkflowComplete: (evt) => {
        const finalStatus = (evt && evt.status ? evt.status : 'completed').toString().toLowerCase();
        setExecutionStatus(finalStatus);
        setExecutionCompletedAt(new Date());
        // Disconnect — the run won't emit more events.
        disconnectWorkflowWebSocket();
      },
    });
    return () => {
      disconnectWorkflowWebSocket();
    };
  }, [executionRunId, executionStatus, applyNodeExecEvent]);

  // Initial render — hydrate from URL workflowId if present, else fall
  // back to the built-in demo fixture. Runs once on mount; subsequent
  // loads happen via explicit handlers.
  const didBootstrapRef = useRef(false);
  useEffect(() => {
    if (didBootstrapRef.current) return;
    didBootstrapRef.current = true;
    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    // Execution view takes precedence — if a runId is in the URL we go
    // straight to the live exec overlay; users can still flip to design
    // by clearing the param. (Convenience aliases: runId, executionId.)
    const urlRunId = params && (params.get('runId') || params.get('executionId'));
    if (urlRunId) {
      loadExecution(urlRunId);
      return;
    }
    const urlWorkflowId = params && params.get('workflowId');
    if (urlWorkflowId) {
      loadWorkflow(urlWorkflowId);
      return;
    }
    runLayout(DEFAULT_FIXTURE, DEFAULT_MODE);
  }, [runLayout, loadWorkflow, loadExecution]);

  // Re-layout whenever the mode flips — reuse whatever payload is loaded.
  const handleModeChange = useCallback((nextMode) => {
    setMode(nextMode);
    runLayout(lastPayloadRef.current, nextMode);
  }, [runLayout]);

  const handleApply = useCallback(() => {
    setParseError(null);
    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch (err) {
      setParseError(err.message || String(err));
      return;
    }
    if (!parsed || typeof parsed !== 'object') {
      setParseError('Expected a JSON object with { nodes, connections }.');
      return;
    }
    if (!Array.isArray(parsed.nodes) || !Array.isArray(parsed.connections)) {
      setParseError('Payload must include array fields `nodes` and `connections`.');
      return;
    }
    runLayout(parsed, mode);
    if (workflowId) markDirty();
  }, [jsonText, runLayout, mode, workflowId, markDirty]);

  const handleLoadFixture = useCallback(() => {
    const text = JSON.stringify(DEFAULT_FIXTURE, null, 2);
    setJsonText(text);
    setParseError(null);
    runLayout(DEFAULT_FIXTURE, mode);
  }, [runLayout, mode]);

  const handleLoadDynFork = useCallback(() => {
    const text = JSON.stringify(DYN_FORK_FIXTURE, null, 2);
    setJsonText(text);
    setParseError(null);
    runLayout(DYN_FORK_FIXTURE, mode);
  }, [runLayout, mode]);

  const handleLoadDoWhile = useCallback(() => {
    const text = JSON.stringify(DO_WHILE_FIXTURE, null, 2);
    setJsonText(text);
    setParseError(null);
    runLayout(DO_WHILE_FIXTURE, mode);
  }, [runLayout, mode]);

  const handleLoadSubWf = useCallback(() => {
    const text = JSON.stringify(SUBWF_FIXTURE, null, 2);
    setJsonText(text);
    setParseError(null);
    runLayout(SUBWF_FIXTURE, mode);
  }, [runLayout, mode]);

  // Complex-stress fixture — loads the full claim_stress_test payload with
  // two drill-in targets (Health Subworkflow + Start Payout Workflow) so
  // the #168 drill-in demo is one click away. Clears any expansion stack
  // from a prior fixture so the load always starts fully collapsed.
  const handleLoadStress = useCallback(() => {
    const text = JSON.stringify(COMPLEX_CLAIM_STRESS_TEST, null, 2);
    setJsonText(text);
    setParseError(null);
    setExpandedSubworkflows(new Set());
    setDiffBasePayload(null);
    runLayout(COMPLEX_CLAIM_STRESS_TEST, mode, undefined, null);
  }, [runLayout, mode]);

  // #170 — version diff demo. Load the current stress-test as the active
  // payload AND a hand-crafted "v3" base fixture. The union layout shows
  // a ReView-style red/green/amber overlay in a single click so the demo
  // doesn't require the reviewer to paste JSON to see the feature.
  const handleDiffDemo = useCallback(() => {
    const text = JSON.stringify(COMPLEX_CLAIM_STRESS_TEST, null, 2);
    setJsonText(text);
    setParseError(null);
    setExpandedSubworkflows(new Set());
    setDiffBasePayload(STRESS_TEST_BASE);
    runLayout(COMPLEX_CLAIM_STRESS_TEST, mode, undefined, STRESS_TEST_BASE);
  }, [runLayout, mode]);

  // Exit diff view — clear the base and re-lay the current payload alone.
  const handleExitDiff = useCallback(() => {
    setDiffBasePayload(null);
    setDiffSummary(null);
    runLayout(lastPayloadRef.current, mode, undefined, null);
  }, [runLayout, mode]);

  // ── SubWorkflow inline drill-in (#168) ─────────────────────────────
  // Clicking a SubWorkflow node toggles its expansion: expanded =>
  // `_embeddedCanvas` is injected from the child registry and the next
  // layout pass nests the child's DAG inside the SubWorkflow container;
  // collapsed => scalar tile. Works at any nesting depth — you can drill
  // into a grandchild once the parent is expanded.
  //
  // We match on the RF node `type` so both the scalar tile
  // (`v2SubWorkflow`) and the container head (`v2SubWorkflowHead`)
  // respond to clicks. `data.sourceNodeId` is the flat-canvas id.
  const handleNodeClick = useCallback((_event, node) => {
    if (!node || !node.type) return;
    const sourceId = node.data && node.data.sourceNodeId;
    if (!sourceId) return;

    // ── SubWorkflow drill-in (#168) — keep existing behaviour ───────
    const isSubWf = node.type === 'v2SubWorkflow' || node.type === 'v2SubWorkflowHead';
    if (isSubWf) {
      const payload = lastPayloadRef.current;
      const src = payload && Array.isArray(payload.nodes)
        ? payload.nodes.find((n) => n && n.id === sourceId)
        : null;
      const cfg = (src && src.config) || {};
      const child = lookupChildCanvas(cfg.subWorkflowName || cfg.name, cfg.subWorkflowVersion, src && src.label);
      if (child) {
        setExpandedSubworkflows((prev) => {
          const next = new Set(prev);
          if (next.has(sourceId)) next.delete(sourceId);
          else next.add(sourceId);
          return next;
        });
        return;
      }
      // No registered child — fall through to config-editor on the
      // subworkflow node itself (user probably wants to edit the ref).
    }

    // ── Node config editor (#181) ───────────────────────────────────
    // Non-subworkflow clicks (or subworkflow clicks without a registered
    // child) open the right-side property panel for the corresponding
    // canvas node. Containers / sentinels / meet dots are skipped —
    // they're structural chrome, not user-editable.
    const SKIP = new Set([
      'v2SwitchContainer', 'v2CaseContainer',
      'v2ForkContainer', 'v2LegContainer', 'v2DynForkContainer',
      'v2LoopContainer', 'v2LoopBody',
      'v2SubWorkflowContainer',
      'v2Meet', 'v2Sentinel',
    ]);
    if (SKIP.has(node.type)) return;

    const payload = lastPayloadRef.current;
    const src = payload && Array.isArray(payload.nodes)
      ? payload.nodes.find((n) => n && n.id === sourceId)
      : null;
    if (!src) return;
    setEditingNode({
      id: src.id,
      type: src.type,
      label: src.label || '',
      config: src.config ? { ...src.config } : {},
    });
  }, []);

  // Commit a panel edit back into the payload + re-layout + mark dirty.
  // The panel hands us the full canvas-shape node; we swap it in by id,
  // preserving any fields the panel didn't touch. V2-only hints are
  // filtered on save by `stripV2OnlyHints`, so we don't worry about
  // them leaking here — whatever the panel emits is what gets rendered.
  const handleNodeConfigApply = useCallback((updatedNode) => {
    const payload = lastPayloadRef.current;
    if (!payload || !Array.isArray(payload.nodes)) return;
    const nextNodes = payload.nodes.map((n) => {
      if (!n || n.id !== updatedNode.id) return n;
      return { ...n, label: updatedNode.label, config: updatedNode.config };
    });
    const nextPayload = { ...payload, nodes: nextNodes };
    runLayout(nextPayload, mode, undefined, diffBasePayload);
    if (workflowId && !isExecutionMode) markDirty();
    setEditingNode(null);
  }, [runLayout, mode, diffBasePayload, workflowId, isExecutionMode, markDirty]);

  // Re-layout whenever the expansion set changes so the rendered DAG
  // reflects the current drill-in stack. We skip the initial mount —
  // the bootstrap effect handles that — and key off set identity.
  const prevExpandedRef = useRef(expandedSubworkflows);
  useEffect(() => {
    if (prevExpandedRef.current === expandedSubworkflows) return;
    prevExpandedRef.current = expandedSubworkflows;
    // Only fire post-bootstrap; the bootstrap effect already laid out.
    if (!didBootstrapRef.current) return;
    runLayout(lastPayloadRef.current, mode, expandedSubworkflows);
  }, [expandedSubworkflows, runLayout, mode]);

  // Collapse all — convenience for the breadcrumb / 'reset' affordance.
  const handleCollapseAll = useCallback(() => {
    setExpandedSubworkflows(new Set());
  }, []);

  // ── Command palette (#171) ─────────────────────────────────────────
  // Global ⌘K / Ctrl+K shortcut. Respects form inputs — if focus is in
  // the JSON editor or any textarea, we don't hijack the keystroke.
  // Also handles ⌘S (#179) to flush pending changes to the backend
  // when a workflow is loaded — the standard save shortcut.
  useEffect(() => {
    const onKey = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      // ⌘K — palette
      if (e.key === 'k' || e.key === 'K') {
        const tgt = e.target;
        const tag = tgt && tgt.tagName ? tgt.tagName.toLowerCase() : '';
        const inEditable =
          tag === 'textarea' ||
          (tag === 'input' && tgt.type !== 'checkbox' && tgt.type !== 'radio') ||
          (tgt && tgt.isContentEditable);
        if (inEditable) return;
        e.preventDefault();
        setCommandPaletteOpen((v) => !v);
        return;
      }
      // ⌘S — save (only when a workflow is loaded; otherwise the OS
      // 'save page' default is fine to fire).
      if ((e.key === 's' || e.key === 'S') && workflowId) {
        e.preventDefault();
        if (!isSavingWorkflow) saveWorkflow();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [workflowId, isSavingWorkflow, saveWorkflow]);

  // Jump-to handler fired by the palette when the user presses Enter on
  // a result. The palette itself already panned the viewport via
  // `setCenter`; here we paint a transient 'v2-flash' className on the
  // targeted rf node so the viewer's attention locks on once the camera
  // settles. The className is picked up by a global keyframe defined in
  // CanvasV2 — no change needed in each individual node renderer.
  const handlePaletteActivate = useCallback((item) => {
    if (!item || !item.id) return;
    setFlashNodeRfId(item.id);
    const t = setTimeout(() => setFlashNodeRfId(null), 1400);
    return () => clearTimeout(t);
  }, []);

  // Attach the 'v2-flash' className to the currently-flashed rf node.
  useEffect(() => {
    setRfNodes((prev) => prev.map((n) => {
      const wantFlash = flashNodeRfId && n.id === flashNodeRfId;
      const hasFlash = n.className && n.className.indexOf('v2-flash') >= 0;
      if (wantFlash && hasFlash) return n;
      if (!wantFlash && !hasFlash) return n;
      const base = (n.className || '').replace(/(^|\s)v2-flash(\s|$)/g, ' ').trim();
      const next = wantFlash ? (base ? `${base} v2-flash` : 'v2-flash') : base;
      return { ...n, className: next || undefined };
    }));
  }, [flashNodeRfId, setRfNodes]);

  // ── Code-tab sync: canvas → JSON (readback, task #163) ─────────────
  // Re-serializes whatever payload the builder last accepted, so the Code
  // panel reflects what the canvas is actually showing (whitespace-normalized,
  // key-ordered). One direction of the bidirectional sync contract; the
  // canvas → JSON readback from interactive graph edits comes with direct-edit
  // tools (tracked under Tier-1 Command Palette + inline edit tickets).
  const handleCanonicalize = useCallback(() => {
    const payload = lastPayloadRef.current || DEFAULT_FIXTURE;
    setJsonText(JSON.stringify(payload, null, 2));
    setParseError(null);
  }, []);

  // ── Execution overlay demo (#163) ──────────────────────────────────
  // Tags a plausible subset of rendered nodes with `data.execStatus` so the
  // V2 node chrome shows running-ring + completed-ring + the top-right
  // ExecBadge. This is a client-only demo; real execution wiring lands when
  // /executions/:id/live streams into Canvas V2.
  const [execDemo, setExecDemo] = useState(false);
  useEffect(() => {
    setRfNodes((prev) => prev.map((n) => {
      if (!execDemo) {
        if (!n.data || !n.data.execStatus) return n;
        const { execStatus, execDurationMs, ...rest } = n.data;
        return { ...n, data: rest };
      }
      const kind = n.data && n.data.kind;
      // Skip container chrome + sentinels — only paint scalar task tiles +
      // heads; that's what the eye will track as work-in-flight.
      if (!kind || kind === 'end' || kind === 'start' || kind === 'meet') return n;
      const status = pickDemoStatus(n.id);
      return {
        ...n,
        data: {
          ...n.data,
          execStatus: status,
          execDurationMs: status === 'completed' ? 320 + (n.id.length * 7) % 800 : null,
        },
      };
    }));
  }, [execDemo, setRfNodes]);

  const stats = useMemo(() => ({
    nodes: rfNodes.length,
    edges: rfEdges.length,
  }), [rfNodes.length, rfEdges.length]);

  return (
    <div style={{
      display: 'flex',
      height: 'calc(100vh - 64px)',
      // Brand-tinted wash — echoes the canvas' own backdrop so the app
      // reads as a single surface instead of a greyscale frame around a
      // branded canvas.
      background: V2_PAL.canvas.background,
    }}>
      {/* Left JSON panel — all chrome pulled from the palette so any brand
          retint propagates from v2Palette.js in one place. */}
      {panelOpen && (
        <div style={{
          width: 420, display: 'flex', flexDirection: 'column',
          borderRight: `1px solid ${V2_STONE[200]}`,
          background: V2_PAL.canvas.panelBg,
        }}>
          <div style={{
            padding: '14px 16px',
            borderBottom: `1px solid ${V2_STONE[200]}`,
            // Subtle brand-gradient heading strip — terracotta hint in the
            // top-left corner, fades to white. Makes the panel feel branded
            // at a glance without shouting.
            background: `linear-gradient(135deg, ${V2_BRAND.soft} 0%, #ffffff 60%)`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          }}>
            <div>
              <div style={{
                fontSize: 14, fontWeight: 700, color: V2_STONE[900],
                letterSpacing: '-0.01em',
              }}>
                Canvas V2 · construct parity
              </div>
              <div style={{ fontSize: 11, color: V2_STONE[500], marginTop: 3 }}>
                Switch · Fork · DynFork · DoWhile · SubWorkflow — tasks #160–#162
              </div>
              <div style={{
                marginTop: 10, display: 'inline-flex',
                border: `1px solid ${V2_STONE[300]}`, borderRadius: 6, overflow: 'hidden',
              }}>
                <button
                  onClick={() => handleModeChange('hierarchical')}
                  disabled={isLaying}
                  style={modeBtn(mode === 'hierarchical')}
                >Hierarchical</button>
                <button
                  onClick={() => handleModeChange('flat')}
                  disabled={isLaying}
                  style={modeBtn(mode === 'flat')}
                >Flat</button>
              </div>
            </div>
            <button
              onClick={() => setPanelOpen(false)}
              style={{
                padding: '4px 8px', fontSize: 11, color: V2_STONE[500],
                background: 'transparent', border: `1px solid ${V2_STONE[300]}`,
                borderRadius: 4, cursor: 'pointer',
              }}
            >hide</button>
          </div>

          {/* #179 — workflow IO strip. Visible only when we've actually
              loaded a workflow from the backend. Surfaces the name, the
              dirty flag, last-saved timestamp, and Save (Save As / Open
              come in #179 follow-up). When no workflowId is set, falls
              back to the original "paste JSON" hint. */}
          {workflowId ? (
            <div style={{
              padding: '10px 12px', borderBottom: `1px solid ${V2_STONE[100]}`,
              display: 'flex', alignItems: 'center', gap: 8,
              background: V2_BRAND.soft,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: V2_STONE[900],
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{workflowName || 'Workflow'}</div>
                <div style={{
                  fontSize: 10, color: V2_STONE[500],
                  fontFamily: 'SFMono-Regular, Menlo, monospace',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {workflowId}
                  {savedAt && (
                    <>
                      {' · '}
                      <span title={`Last saved ${savedAt.toISOString()}`}>
                        saved {formatRelative(savedAt)}
                      </span>
                    </>
                  )}
                </div>
              </div>
              {dirty && (
                <span title="Unsaved changes" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 7px', fontSize: 10, fontWeight: 600,
                  color: '#92400e', background: '#fef3c7',
                  border: '1px solid #fcd34d', borderRadius: 999,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b' }} />
                  unsaved
                </span>
              )}
              <button
                onClick={saveWorkflow}
                disabled={isSavingWorkflow || !dirty || isExecutionMode}
                title={
                  isExecutionMode ? 'Execution view is read-only' :
                  dirty ? 'Save canvas changes' : 'Nothing to save'
                }
                style={{
                  padding: '5px 10px', fontSize: 11, fontWeight: 600,
                  color: '#ffffff',
                  background: (isSavingWorkflow || !dirty || isExecutionMode) ? V2_STONE[400] : V2_BRAND.primary,
                  border: 'none', borderRadius: 5,
                  cursor: (isSavingWorkflow || !dirty || isExecutionMode) ? 'default' : 'pointer',
                }}
              >{isSavingWorkflow ? 'Saving…' : (isExecutionMode ? 'Read-only' : 'Save')}</button>
            </div>
          ) : (
            <div style={{
              padding: 12, fontSize: 12, color: V2_STONE[600],
              borderBottom: `1px solid ${V2_STONE[100]}`,
            }}>
              Paste a workflow <code>{`{nodes, connections}`}</code> payload
              (same shape the right-panel Code tab emits on <code>/canvas</code>),
              then press <b>Apply</b>. Or open a saved workflow with
              {' '}<code>?workflowId=…</code>.
            </div>
          )}
          {/* Save / load status line — surfaces success or error from the
              most recent backend round-trip. Auto-clears on next action. */}
          {saveStatus && (
            <div style={{
              padding: '6px 12px', fontSize: 11,
              color: saveStatus.kind === 'ok' ? '#166534' : '#9f1239',
              background: saveStatus.kind === 'ok' ? '#dcfce7' : '#fef2f2',
              borderBottom: `1px solid ${V2_STONE[100]}`,
            }}>{saveStatus.message}</div>
          )}
          {(isLoadingWorkflow) && (
            <div style={{
              padding: '6px 12px', fontSize: 11, color: V2_STONE[500],
              borderBottom: `1px solid ${V2_STONE[100]}`,
            }}>Loading workflow…</div>
          )}

          <textarea
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            spellCheck={false}
            style={{
              flex: 1, margin: 12, padding: 10, fontSize: 11,
              fontFamily: 'SFMono-Regular, Menlo, monospace',
              border: `1px solid ${V2_STONE[300]}`, borderRadius: 6,
              resize: 'none', outline: 'none', color: V2_STONE[900],
              background: '#ffffff',
            }}
          />

          {parseError && (
            <div style={{
              margin: '0 12px 12px', padding: 10, fontSize: 11,
              background: '#fff1f2', color: '#9f1239',
              border: '1px solid #fecdd3', borderRadius: 6,
            }}>
              <b>Parse error:</b> {parseError}
            </div>
          )}
          {layoutError && (
            <div style={{
              margin: '0 12px 12px', padding: 10, fontSize: 11,
              background: '#fffbeb', color: '#92400e',
              border: '1px solid #fde68a', borderRadius: 6,
            }}>
              <b>Layout error:</b> {layoutError}
            </div>
          )}
          {/* Payload-level lint banner — surfaces canvas-wide diagnostics
              (lint/parse-failed, lint/malformed-payload) that don't belong
              to any single node. Per-node diagnostics render as badges on
              the node itself. */}
          {payloadDiagnostics.length > 0 && (
            <div style={{
              margin: '0 12px 12px', padding: 10, fontSize: 11,
              background: '#fef2f2', color: '#9f1239',
              border: '1px solid #fecdd3', borderRadius: 6,
            }}>
              <b>Lint:</b>
              <ul style={{ margin: '6px 0 0', paddingLeft: 16 }}>
                {payloadDiagnostics.map((d, i) => (
                  <li key={`${d.rule}-${i}`} style={{ marginBottom: 3 }}>
                    <code style={{ fontSize: 10, color: '#7f1d1d' }}>{d.rule}</code>
                    <span style={{ marginLeft: 6 }}>{d.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{
            padding: 12, borderTop: `1px solid ${V2_STONE[100]}`,
            display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            {/* Apply — primary action gets the BRAND gradient so the call to
                action wears the Agentlets identity.
                Canonicalize — readback of whatever payload the canvas is
                actually showing. Closes the bidirectional Code-tab loop
                (task #163): users can see what the builder parsed, key
                ordering and all, without stepping outside the panel. */}
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={handleApply}
                disabled={isLaying}
                style={{
                  flex: 1,
                  padding: '10px 14px', fontSize: 13, fontWeight: 600,
                  color: '#ffffff',
                  background: isLaying ? V2_STONE[400] : V2_BRAND.gradient,
                  border: 'none', borderRadius: 6,
                  cursor: isLaying ? 'default' : 'pointer',
                  boxShadow: isLaying ? 'none' : `0 1px 3px ${V2_BRAND.glow}`,
                }}
              >{isLaying ? 'Laying out…' : 'Apply + Layout'}</button>
              <button
                onClick={handleCanonicalize}
                disabled={isLaying}
                title="Re-serialize the currently-applied payload into the editor"
                style={{
                  padding: '10px 12px', fontSize: 12, fontWeight: 600,
                  color: V2_STONE[700],
                  background: '#ffffff',
                  border: `1px solid ${V2_STONE[300]}`, borderRadius: 6,
                  cursor: isLaying ? 'default' : 'pointer',
                }}
              >Canonicalize ↻</button>
            </div>

            {/* Fixture quick-load row — one construct per button. Each wears
                its construct's chrome tint so it's immediately recognisable. */}
            <div style={{
              fontSize: 10, fontWeight: 600, letterSpacing: '0.04em',
              color: V2_STONE[500], textTransform: 'uppercase',
            }}>Load a fixture</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <button
                onClick={handleLoadFixture}
                disabled={isLaying}
                style={fixtureBtn(V2_PAL.switch.head.bg, V2_PAL.switch.head.border, V2_PAL.switch.head.text)}
                title="Start → Fork → Join → Switch → End"
              >Fork + Switch</button>
              <button
                onClick={handleLoadDynFork}
                disabled={isLaying}
                style={fixtureBtn(V2_PAL.dynFork.head.bg, V2_PAL.dynFork.head.border, V2_PAL.dynFork.head.text)}
                title="Dynamic fork with listExpression + template leg"
              >Dyn-fork</button>
              <button
                onClick={handleLoadDoWhile}
                disabled={isLaying}
                style={fixtureBtn(V2_PAL.loop.head.bg, V2_PAL.loop.head.border, V2_PAL.loop.head.text)}
                title="DoWhile container with dashed orange back-edge"
              >DoWhile</button>
              <button
                onClick={handleLoadSubWf}
                disabled={isLaying}
                style={fixtureBtn(V2_PAL.subWorkflow.sync.bg, V2_PAL.subWorkflow.sync.border, V2_PAL.subWorkflow.sync.text)}
                title="SubWorkflow with inline-expanded preview body (drill-in)"
              >SubWorkflow</button>
            </div>
            {/* Full claim stress-test — the workflow with two drill-in
                targets (Health Subworkflow + Start Payout Workflow).
                Click either after loading to see #168 nested rendering. */}
            <button
              onClick={handleLoadStress}
              disabled={isLaying}
              style={{
                ...fixtureBtn(V2_BRAND.soft, V2_BRAND.muted, V2_BRAND.dark),
                padding: '10px 12px', fontSize: 12,
              }}
              title="Complex claim stress test — click Health Subworkflow or Start Payout Workflow on the canvas to drill in"
            >Complex claim stress (drill-in demo)</button>

            {/* Version-diff demo (#170). Loads the stress-test payload
                as "current" + a hand-crafted "v3" base, and shows a
                red/green/amber overlay. Exit via the banner's exit btn. */}
            <button
              onClick={handleDiffDemo}
              disabled={isLaying}
              style={{
                padding: '10px 12px', fontSize: 12, fontWeight: 600,
                color: '#0f172a',
                background: 'linear-gradient(135deg, #dcfce7 0%, #fef3c7 50%, #fee2e2 100%)',
                border: `1px solid ${V2_STONE[300]}`, borderRadius: 6,
                cursor: isLaying ? 'default' : 'pointer',
              }}
              title="Version diff — stress-test v3 (base) vs current. Shows added / removed / modified overlays in one glance."
            >Version diff (v3 → current)</button>

            {/* Execution overlay demo toggle (#163). Paints exec status
                rings + badges without wiring to a real execution. */}
            <label style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 11, color: V2_STONE[700],
              padding: '6px 0', marginTop: 2,
              borderTop: `1px dashed ${V2_STONE[200]}`, paddingTop: 10,
            }}>
              <input
                type="checkbox"
                checked={execDemo}
                onChange={(e) => setExecDemo(e.target.checked)}
                style={{ accentColor: V2_BRAND.primary }}
              />
              <span>
                <b style={{ color: V2_STONE[900] }}>Execution overlay</b>
                <span style={{ color: V2_STONE[500] }}> — paint live-status rings & badges</span>
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Canvas */}
      <div style={{ flex: 1, position: 'relative' }}>
        {!panelOpen && (
          <button
            onClick={() => setPanelOpen(true)}
            style={{
              position: 'absolute', top: 12, left: 12, zIndex: 10,
              padding: '6px 10px', fontSize: 11, color: V2_STONE[900],
              background: '#ffffff', border: `1px solid ${V2_STONE[300]}`,
              borderRadius: 4, cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
            }}
          >show JSON panel</button>
        )}

        {/* Stats badge — brand-tinted pill so the numbers feel part of the
            canvas chrome, not a generic dev overlay. */}
        {/* Execution overlay banner (#180). Visible whenever the page is
            in execution-view mode (URL had ?runId= on mount, or user
            navigated into a run). Live runs pulse the dot; finished runs
            freeze it green/red/grey. Sits at top so it never collides
            with the drill-in breadcrumb or diff banner below. */}
        {isExecutionMode && (
          <div style={{
            position: 'absolute', top: 12, left: 12, zIndex: 12,
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 12px', fontSize: 11,
            background: 'rgba(255,255,255,0.96)',
            border: `1px solid ${V2_STONE[300]}`,
            borderRadius: 999,
            boxShadow: '0 1px 2px rgba(15,23,42,0.08)',
          }}>
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: V2_STONE[600],
              padding: '2px 7px', border: `1px solid ${V2_STONE[300]}`,
              borderRadius: 4, background: V2_STONE[100],
            }}>Execution</span>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontWeight: 600,
              color:
                executionStatus === 'completed' ? '#166534' :
                executionStatus === 'failed' ? '#991b1b' :
                executionStatus === 'cancelled' ? V2_STONE[600] :
                '#0c4a6e',
            }}>
              <span
                className={executionStatus === 'running' ? 'v2-exec-pulse' : undefined}
                style={{
                  width: 9, height: 9, borderRadius: '50%',
                  background:
                    executionStatus === 'completed' ? '#16a34a' :
                    executionStatus === 'failed' ? '#dc2626' :
                    executionStatus === 'cancelled' ? V2_STONE[400] :
                    '#0ea5e9',
                }}
              />
              {executionStatus
                ? executionStatus.charAt(0).toUpperCase() + executionStatus.slice(1)
                : 'Loading…'}
            </span>
            {executionStartedAt && (
              <span style={{ color: V2_STONE[500] }}>
                started {formatRelative(executionStartedAt)}
                {executionCompletedAt && (
                  <>
                    {' · '}
                    <span title={`Completed ${executionCompletedAt.toISOString()}`}>
                      took {formatDuration(executionCompletedAt.getTime() - executionStartedAt.getTime())}
                    </span>
                  </>
                )}
              </span>
            )}
            <span style={{
              fontSize: 10, color: V2_STONE[500],
              fontFamily: 'SFMono-Regular, Menlo, monospace',
              padding: '1px 6px', background: V2_STONE[100], borderRadius: 4,
            }}>{executionRunId}</span>
          </div>
        )}

        {/* Version-diff summary banner (#170). Shows up whenever a diff
            base is set, giving a one-glance summary and an exit button.
            Stacks above the drill-in breadcrumb so both can coexist. */}
        {diffSummary && (
          <div style={{
            position: 'absolute', top: 12,
            left: expandedSubworkflows.size > 0 ? 12 : 12,
            zIndex: 11,
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 10px', fontSize: 11,
            background: 'rgba(255,255,255,0.96)',
            border: `1px solid ${V2_STONE[300]}`,
            borderRadius: 999,
            boxShadow: '0 1px 2px rgba(15,23,42,0.08)',
            transform: expandedSubworkflows.size > 0 ? 'translateY(36px)' : 'translateY(0)',
          }}>
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: V2_STONE[600],
              padding: '2px 7px', border: `1px solid ${V2_STONE[300]}`,
              borderRadius: 4, background: V2_STONE[100],
            }}>Diff</span>
            {diffSummary.counts.added > 0 && (
              <span title={`${diffSummary.counts.added} added`} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                color: '#166534', fontWeight: 600,
              }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a' }} />
                +{diffSummary.counts.added}
              </span>
            )}
            {diffSummary.counts.removed > 0 && (
              <span title={`${diffSummary.counts.removed} removed`} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                color: '#991b1b', fontWeight: 600,
              }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#dc2626' }} />
                −{diffSummary.counts.removed}
              </span>
            )}
            {diffSummary.counts.modified > 0 && (
              <span title={`${diffSummary.counts.modified} modified`} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                color: '#92400e', fontWeight: 600,
              }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#d97706' }} />
                ~{diffSummary.counts.modified}
              </span>
            )}
            {(diffSummary.counts.added + diffSummary.counts.removed + diffSummary.counts.modified) === 0 && (
              <span style={{ color: V2_STONE[500] }}>no changes</span>
            )}
            <button
              onClick={handleExitDiff}
              title="Exit diff view"
              style={{
                padding: '2px 8px', fontSize: 10, fontWeight: 500,
                color: V2_STONE[600],
                background: 'transparent',
                border: `1px dashed ${V2_STONE[300]}`,
                borderRadius: 6, cursor: 'pointer', marginLeft: 4,
              }}
            >exit</button>
          </div>
        )}

        {/* SubWorkflow drill-in breadcrumb (#168). Surfaces the expansion
            stack so the viewer always knows where they are. Click a pill
            to collapse that subworkflow; click "collapse all" to reset. */}
        {expandedSubworkflows.size > 0 && (
          <div style={{
            position: 'absolute', top: 12, left: 12, zIndex: 10,
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '5px 10px', fontSize: 11,
            background: 'rgba(255,255,255,0.94)',
            border: `1px solid ${V2_PAL.subWorkflow.sync.border}`,
            borderRadius: 999,
            boxShadow: `0 1px 2px rgba(99,102,241,0.18)`,
            maxWidth: 'calc(100% - 260px)',
          }}>
            <span style={{
              color: V2_PAL.subWorkflow.sync.accent,
              fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
              fontSize: 9,
            }}>Drilled in</span>
            {[...expandedSubworkflows].map((sourceId) => {
              const payload = lastPayloadRef.current;
              const src = payload && Array.isArray(payload.nodes)
                ? payload.nodes.find((n) => n && n.id === sourceId) : null;
              const label = (src && src.label) || sourceId;
              return (
                <button
                  key={sourceId}
                  onClick={() => setExpandedSubworkflows((prev) => {
                    const next = new Set(prev);
                    next.delete(sourceId);
                    return next;
                  })}
                  title={`Collapse ${label}`}
                  style={{
                    padding: '2px 8px', fontSize: 11, fontWeight: 600,
                    color: V2_PAL.subWorkflow.sync.text,
                    background: V2_PAL.subWorkflow.sync.bg,
                    border: `1px solid ${V2_PAL.subWorkflow.sync.border}`,
                    borderRadius: 6, cursor: 'pointer',
                    maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >{label} ×</button>
              );
            })}
            <button
              onClick={handleCollapseAll}
              title="Collapse all expanded sub-workflows"
              style={{
                padding: '2px 8px', fontSize: 10, fontWeight: 500,
                color: V2_STONE[600],
                background: 'transparent',
                border: `1px dashed ${V2_STONE[300]}`,
                borderRadius: 6, cursor: 'pointer',
              }}
            >collapse all</button>
          </div>
        )}

        <div style={{
          position: 'absolute', top: 12, right: 12, zIndex: 10,
          padding: '5px 12px', fontSize: 11, fontWeight: 500,
          color: V2_BRAND.dark,
          background: 'rgba(255,255,255,0.92)',
          border: `1px solid ${V2_BRAND.muted}`, borderRadius: 999,
          boxShadow: `0 1px 2px ${V2_BRAND.glow}`,
        }}>
          <span style={{ color: V2_STONE[900], fontWeight: 700 }}>{stats.nodes}</span>
          <span style={{ color: V2_STONE[500] }}> nodes · </span>
          <span style={{ color: V2_STONE[900], fontWeight: 700 }}>{stats.edges}</span>
          <span style={{ color: V2_STONE[500] }}> edges</span>
          {execDemo && (
            <span style={{
              marginLeft: 8, paddingLeft: 8,
              borderLeft: `1px solid ${V2_STONE[200]}`,
              color: V2_BRAND.primary, fontWeight: 600,
            }}>● exec overlay</span>
          )}
          {/* ⌘K shortcut hint (#171) — clickable, so palette is discoverable
              for non-keyboard users too. */}
          <span
            onClick={() => setCommandPaletteOpen(true)}
            title="Open the command palette — jump to any node"
            style={{
              marginLeft: 8, paddingLeft: 8,
              borderLeft: `1px solid ${V2_STONE[200]}`,
              cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <span style={{
              fontFamily: 'SFMono-Regular, Menlo, monospace',
              fontSize: 10, fontWeight: 600,
              color: V2_BRAND.primary,
              padding: '1px 6px', border: `1px solid ${V2_BRAND.muted}`,
              borderRadius: 4, background: '#ffffff',
            }}>⌘K</span>
            <span style={{ color: V2_STONE[500] }}>jump to…</span>
          </span>
          {(lintSummary.error + lintSummary.warn + lintSummary.info) > 0 && (
            <span
              title="Semantic-lint findings — hover a node to see the rule"
              style={{
                marginLeft: 8, paddingLeft: 8,
                borderLeft: `1px solid ${V2_STONE[200]}`,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              {lintSummary.error > 0 && (
                <span style={{ color: '#e11d48', fontWeight: 700 }}>
                  ● {lintSummary.error}<span style={{ fontWeight: 500, opacity: 0.85 }}> err</span>
                </span>
              )}
              {lintSummary.warn > 0 && (
                <span style={{ color: '#d97706', fontWeight: 700 }}>
                  ● {lintSummary.warn}<span style={{ fontWeight: 500, opacity: 0.85 }}> warn</span>
                </span>
              )}
              {lintSummary.info > 0 && (
                <span style={{ color: '#0284c7', fontWeight: 700 }}>
                  ● {lintSummary.info}<span style={{ fontWeight: 500, opacity: 0.85 }}> info</span>
                </span>
              )}
            </span>
          )}
        </div>

        {/* Global canvas styles — flash keyframe for the palette jump
            and version-diff tinting (#170/#171). Scoped via `.react-flow`
            prefixes so nothing leaks to the rest of the app. */}
        <style>{`
          @keyframes v2FlashPulse {
            0%   { box-shadow: 0 0 0 0 rgba(226, 74, 58, 0.55); }
            60%  { box-shadow: 0 0 0 10px rgba(226, 74, 58, 0.08); }
            100% { box-shadow: 0 0 0 0 rgba(226, 74, 58, 0); }
          }
          .react-flow__node.v2-flash > div {
            animation: v2FlashPulse 1.35s ease-out;
            border-radius: 12px;
          }
          /* Version-diff tint rings. Each status gets a 4px outer ring
             so the underlying node chrome (amber switch, teal fork, etc.)
             still reads. Removed nodes dim to 55% so they visually
             recede — they're "where it used to be," not live. */
          .react-flow__node.v2-diff-added > div {
            box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.85), 0 0 0 6px rgba(22, 163, 74, 0.18);
            border-radius: 12px;
          }
          .react-flow__node.v2-diff-removed > div {
            box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.85), 0 0 0 6px rgba(220, 38, 38, 0.16);
            border-radius: 12px;
            opacity: 0.55;
            filter: saturate(0.7);
          }
          .react-flow__node.v2-diff-modified > div {
            box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.85), 0 0 0 6px rgba(217, 119, 6, 0.18);
            border-radius: 12px;
          }
          /* Live execution dot pulse (#180) — used in the exec banner. */
          @keyframes v2ExecPulse {
            0%, 100% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(14, 165, 233, 0.55); }
            50%      { transform: scale(1.18); opacity: 0.85; box-shadow: 0 0 0 6px rgba(14, 165, 233, 0); }
          }
          .v2-exec-pulse {
            animation: v2ExecPulse 1.4s ease-in-out infinite;
          }
        `}</style>

        <CommandPalette
          open={commandPaletteOpen}
          onClose={() => setCommandPaletteOpen(false)}
          rfNodes={rfNodes}
          onActivate={handlePaletteActivate}
        />

        <NodeConfigPanel
          editingNode={editingNode}
          readOnly={isExecutionMode}
          onChange={handleNodeConfigApply}
          onClose={() => setEditingNode(null)}
        />

        <ReactFlow
          nodes={rfNodes}
          edges={rfEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={handleNodeClick}
          nodeTypes={mode === 'hierarchical' ? V2_NODE_TYPES : undefined}
          edgeTypes={mode === 'hierarchical' ? V2_EDGE_TYPES : undefined}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          proOptions={{ hideAttribution: true }}
          defaultEdgeOptions={{ style: { strokeWidth: 1.5, stroke: '#475569' } }}
        >
          {/* Shared arrow marker for V2 edges. Declared once in the SVG
              defs so every edge can reference url(#v2-arrow). */}
          <svg style={{ position: 'absolute', width: 0, height: 0 }}>
            <defs>
              <marker
                id="v2-arrow"
                viewBox="0 0 10 10"
                refX="8"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#475569" />
              </marker>
              {/* Second marker tinted for the DoWhile back-edge so the arrow
                  tip carries the loop colour rather than the generic slate. */}
              <marker
                id="v2-arrow-loop"
                viewBox="0 0 10 10"
                refX="8"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#ea580c" />
              </marker>
            </defs>
          </svg>
          <Background color={V2_STONE[200]} gap={24} />
          <Controls showInteractive={false} />
          <MiniMap pannable zoomable maskColor="rgba(15,23,42,0.08)" />
        </ReactFlow>
      </div>
    </div>
  );
}

// Deterministic status-per-node so the demo overlay is stable across
// re-renders (no dancing badges). Hashes the node id to a 5-way bucket.
function pickDemoStatus(id) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  const bucket = Math.abs(h) % 10;
  if (bucket < 6) return 'completed';    // 60% done — most work is in the past
  if (bucket < 7) return 'running';      // 10% running
  if (bucket < 8) return 'failed';       // 10% failed
  if (bucket < 9) return 'waiting';      // 10% waiting
  return 'skipped';                       // 10% skipped
}

function modeBtn(active) {
  return {
    padding: '5px 12px',
    fontSize: 11,
    fontWeight: 600,
    color: active ? '#ffffff' : V2_STONE[700],
    // Active state wears the brand gradient so the chosen mode feels
    // primary rather than merely checked.
    background: active ? V2_BRAND.gradient : V2_STONE[50],
    border: 'none',
    borderRight: `1px solid ${V2_STONE[300]}`,
    cursor: 'pointer',
  };
}

// Fixture quick-load button — each carries the construct's chrome tint so
// the palette teaches which construct each fixture exercises before you
// even click.
function fixtureBtn(bg, border, text) {
  return {
    padding: '8px 10px',
    fontSize: 12,
    fontWeight: 600,
    color: text,
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: 6,
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'transform 80ms ease',
  };
}

export default function CanvasPageV2() {
  return (
    <ReactFlowProvider>
      <CanvasV2Inner />
    </ReactFlowProvider>
  );
}
